import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SUPPORTED_LOCALES } from '@/lib/i18n'

import { useAdminContext } from '../session'
import { useCollection } from '../useCollection'
import type { I18n } from '../types'
import { CollectionEditor, FormActions } from './CollectionEditor'
import { I18nField, SwitchField, TextField, type I18nValue } from './fields'

/** Miroir de `FaqOut` (backend/app/schemas/content.py). */
interface Faq {
  id: string
  question_i18n: I18n
  answer_i18n: I18n
  category: string | null
  position: number
  is_visible: boolean
}

/**
 * Les questions fréquentes de l'édition.
 *
 * L'API existait, la page « Infos » du site sait les afficher — et aucun écran
 * ne permettait d'en écrire une. Répondre « où se garer ? » imposait de passer
 * par la base de données, si bien que personne ne le faisait et que les mêmes
 * questions revenaient par WhatsApp la semaine de la soirée.
 *
 * Seules les questions **visibles** partent sur le site, dans l'ordre de la
 * liste. Masquer plutôt que supprimer : une réponse saisonnière (« navette
 * depuis la gare ») se réactive l'année suivante sans être réécrite.
 *
 * Les droits ne servent qu'à masquer ce que le serveur refuserait de toute
 * façon : sans `event.content.update`, la fiche s'ouvre en lecture.
 */
export function FaqEditor({ eventId }: { eventId: string | undefined }) {
  const { t, i18n } = useTranslation()
  const { can } = useAdminContext()

  const faqs = useCollection<Faq>({
    key: 'admin-faqs',
    path: `/admin/events/${eventId}/faqs`,
    eventId,
    related: [['public-faqs']],
  })

  const canCreate = can('event.content.create')
  const canUpdate = can('event.content.update')
  const canDelete = can('event.content.delete')

  function text(value: I18n): string {
    return value[i18n.language] || value[Object.keys(value)[0] ?? ''] || ''
  }

  return (
    <CollectionEditor<Faq>
      title={t('admin.faq.title')}
      description={t('admin.faq.hint')}
      items={faqs.items}
      busy={faqs.busy}
      error={faqs.error}
      emptyLabel={t('admin.faq.empty')}
      createLabel={canCreate ? t('admin.faq.add') : null}
      {...(canDelete ? { onDelete: (faq: Faq) => faqs.remove.mutate({ id: faq.id }) } : {})}
      {...(canUpdate ? { onReorder: (ids: string[]) => faqs.reorder.mutate({ ids }) } : {})}
      summarize={(faq) => {
        // Une langue vide n'empêche pas l'enregistrement (le serveur exige
        // seulement la langue par défaut) : le visiteur de cette langue
        // lirait alors la réponse dans une autre. On le signale dans la liste.
        const missing = (SUPPORTED_LOCALES as readonly string[]).filter(
          (locale) => !faq.question_i18n[locale]?.trim() || !faq.answer_i18n[locale]?.trim(),
        )
        return {
          title: text(faq.question_i18n) || t('admin.faq.untitled'),
          subtitle: faq.category ?? '',
          dimmed: !faq.is_visible,
          badges: [
            ...(faq.is_visible ? [] : [{ label: t('admin.hidden') }]),
            ...(missing.length > 0
              ? [
                  {
                    label: t('admin.faq.missingLocales', {
                      locales: missing.map((locale) => locale.toUpperCase()).join(' · '),
                    }),
                    tone: 'warning' as const,
                  },
                ]
              : []),
          ],
        }
      }}
      renderForm={(faq, close) =>
        faq && !canUpdate ? (
          <FaqReadOnly faq={faq} text={text} />
        ) : (
          <FaqForm
            faq={faq}
            busy={faqs.busy}
            onCancel={close}
            onSubmit={(payload) => {
              const done = { onSuccess: () => close() }
              if (faq) faqs.update.mutate({ id: faq.id, ...payload }, done)
              else faqs.create.mutate(payload, done)
            }}
          />
        )
      }
    />
  )
}

interface FaqPayload {
  question_i18n: I18nValue
  answer_i18n: I18nValue
  category: string | null
  is_visible: boolean
}

function FaqForm({
  faq,
  busy,
  onSubmit,
  onCancel,
}: {
  faq: Faq | null
  busy: boolean
  onSubmit: (payload: FaqPayload) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [question, setQuestion] = useState<I18nValue>(faq?.question_i18n ?? {})
  const [answer, setAnswer] = useState<I18nValue>(faq?.answer_i18n ?? {})
  const [category, setCategory] = useState(faq?.category ?? '')
  const [visible, setVisible] = useState(faq?.is_visible ?? true)

  // Une question sans réponse (ou l'inverse) n'a rien à faire sur le site :
  // le serveur la refuserait, autant ne pas proposer de l'envoyer.
  const filled = (value: I18nValue) => Object.values(value).some((entry) => entry.trim())
  const incomplete = !filled(question) || !filled(answer)

  return (
    <div>
      <div className="grid gap-5">
        <I18nField label={t('admin.faq.question')} value={question} onChange={setQuestion} />
        <I18nField
          label={t('admin.faq.answer')}
          multiline
          value={answer}
          onChange={setAnswer}
        />
        <div className="grid gap-5 sm:grid-cols-2">
          <TextField
            label={t('admin.faq.category')}
            hint={t('admin.faq.categoryHint')}
            value={category}
            onChange={setCategory}
          />
          <SwitchField
            label={t('admin.faq.visible')}
            hint={t('admin.faq.visibleHint')}
            value={visible}
            onChange={setVisible}
          />
        </div>
      </div>
      {incomplete ? (
        <p className="mt-3 text-xs text-content-muted">{t('admin.faq.incomplete')}</p>
      ) : null}
      <FormActions
        busy={busy || incomplete}
        onCancel={onCancel}
        onSubmit={() =>
          onSubmit({
            question_i18n: question,
            answer_i18n: answer,
            // Vide = sans catégorie, pas une catégorie « » qui créerait un
            // intertitre blanc sur le site.
            category: category.trim().slice(0, 80) || null,
            is_visible: visible,
          })
        }
      />
    </div>
  )
}

/** Fiche en lecture seule, pour un compte qui peut lire sans modifier. */
function FaqReadOnly({ faq, text }: { faq: Faq; text: (value: I18n) => string }) {
  const { t } = useTranslation()
  return (
    <div className="grid gap-2 text-sm">
      <p className="text-content-primary">{text(faq.question_i18n)}</p>
      <p className="whitespace-pre-line break-words text-content-secondary">
        {text(faq.answer_i18n)}
      </p>
      <p className="text-xs text-content-muted">{t('admin.faq.readOnly')}</p>
    </div>
  )
}
